import { getTipImage, registerTipImage } from '../brush/library';
import { presetFromOptions, type BrushPreset, type TipImage } from '../brush/types';
import { createId } from '../core/ids';
import { isPaintTool, type Rect } from '../core/types';
import { getEngine } from '../engine/engine';
import { parseAbr, writeAbr } from '../io/abr';
import { downloadBlob } from '../io/export';
import { activeDocument } from './actions';
import { getPaintOptions, useAppStore } from './store';

const engine = getEngine();
/** Sampled tips larger than this gain nothing and cost a lot of texture memory. */
const MAX_TIP_SIZE = 512;

function state() {
  return useAppStore.getState();
}

function activePaintTool() {
  const { tool } = state();
  return isPaintTool(tool) ? tool : 'brush';
}

/** File > Open with an `.abr` file, and the Load button of the Brush panel. */
export async function importBrushFile(file: File): Promise<void> {
  try {
    const buffer = await file.arrayBuffer();
    const name = file.name.replace(/\.[^.]+$/, '');
    const result = parseAbr(buffer, name);
    for (const tip of result.tips) registerTipImage(tip);
    if (result.presets.length === 0) {
      state().setStatus(`No usable brushes in ${file.name}`);
      return;
    }
    state().addBrushPresets(result.presets);
    state().openPanel('brush');
    const skipped = result.skipped > 0 ? `, ${result.skipped} skipped` : '';
    state().setStatus(`Loaded ${result.presets.length} brushes from ${file.name}${skipped}`);
  } catch (error) {
    state().setStatus(error instanceof Error ? error.message : 'Could not read the brush file');
  }
}

/** Writes every sampled preset back out as an ABR file. */
export function exportBrushPresets(presets: BrushPreset[]): void {
  const brushes = presets
    .map((preset) => {
      const tip = getTipImage(preset.tip.imageId);
      return tip ? { name: preset.name, tip, spacing: preset.tip.spacing } : null;
    })
    .filter((entry): entry is { name: string; tip: TipImage; spacing: number } => entry !== null);

  if (brushes.length === 0) {
    state().setStatus('ABR stores sampled tips only, and none of the presets has one');
    return;
  }
  downloadBlob(new Blob([writeAbr(brushes)], { type: 'application/octet-stream' }), 'brushes.abr');
  state().setStatus(`Saved ${brushes.length} brushes as brushes.abr`);
}

/**
 * Edit > Define New > Brush. Turns the active layer, cropped to the selection if
 * there is one, into a sampled tip: dark and opaque pixels paint, light and
 * transparent ones do not.
 */
export function defineBrushFromLayer(): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const resources = engine.peek(doc.id);
  const surface = resources?.layer(doc.activeLayerId)?.surface;
  if (!surface) {
    state().setStatus('Select a pixel layer to define a brush from');
    return;
  }

  const bounds = resources.selection.isEmpty ? null : resources.selection.bounds();
  const rect = clampRect(bounds ?? { x: 0, y: 0, width: doc.width, height: doc.height }, doc);
  if (rect.width < 1 || rect.height < 1) return;

  const pixels = surface.readPixels(rect);
  const trimmed = trimTransparent(pixels, rect);
  if (!trimmed) {
    state().setStatus('The layer is empty inside the selection');
    return;
  }

  const tip = downscale(trimmed, MAX_TIP_SIZE);
  const image: TipImage = {
    id: createId('tip'),
    name: 'Layer Brush',
    width: tip.width,
    height: tip.height,
    data: tip.data,
  };
  registerTipImage(image);

  const tool = activePaintTool();
  const options = getPaintOptions(state(), tool);
  const preset = presetFromOptions(createId('preset'), image.name, {
    ...options,
    size: Math.max(tip.width, tip.height),
    tipImageId: image.id,
  });
  state().addBrushPresets([preset]);
  state().applyBrushPreset(tool, preset);
  state().openPanel('brush');
  state().setStatus(`Defined a ${tip.width}×${tip.height} brush from the layer`);
}

function clampRect(rect: Rect, doc: { width: number; height: number }): Rect {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  return {
    x,
    y,
    width: Math.min(doc.width - x, Math.ceil(rect.width)),
    height: Math.min(doc.height - y, Math.ceil(rect.height)),
  };
}

interface Grey {
  width: number;
  height: number;
  data: Uint8Array;
}

/**
 * Converts RGBA pixels to coverage and drops fully empty rows and columns, the
 * way Photoshop trims a defined brush to its content.
 */
function trimTransparent(pixels: Uint8Array, rect: Rect): Grey | null {
  const coverage = new Uint8Array(rect.width * rect.height);
  let minX = rect.width;
  let minY = rect.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < rect.height; y += 1) {
    for (let x = 0; x < rect.width; x += 1) {
      const index = (y * rect.width + x) * 4;
      const alpha = pixels[index + 3] / 255;
      if (alpha <= 0) continue;
      const lum =
        (0.2126 * pixels[index] + 0.7152 * pixels[index + 1] + 0.0722 * pixels[index + 2]) / 255;
      const value = Math.round(alpha * (1 - lum) * 255);
      coverage[y * rect.width + x] = value;
      if (value <= 0) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;

  const width = maxX - minX + 1;
  const height = maxY - minY + 1;
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      data[y * width + x] = coverage[(y + minY) * rect.width + x + minX];
    }
  }
  return { width, height, data };
}

/** Box filter down to `limit` on the longer side; smaller images pass through. */
function downscale(image: Grey, limit: number): Grey {
  const scale = Math.min(1, limit / Math.max(image.width, image.height));
  if (scale >= 1) return image;

  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const data = new Uint8Array(width * height);
  const stepX = image.width / width;
  const stepY = image.height / height;

  for (let y = 0; y < height; y += 1) {
    const y0 = Math.floor(y * stepY);
    const y1 = Math.min(image.height, Math.ceil((y + 1) * stepY));
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.floor(x * stepX);
      const x1 = Math.min(image.width, Math.ceil((x + 1) * stepX));
      let sum = 0;
      let count = 0;
      for (let sy = y0; sy < y1; sy += 1) {
        for (let sx = x0; sx < x1; sx += 1) {
          sum += image.data[sy * image.width + sx];
          count += 1;
        }
      }
      data[y * width + x] = count > 0 ? Math.round(sum / count) : 0;
    }
  }
  return { width, height, data };
}
